import React, { useRef, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Brush,
  Bar,
} from "recharts";
import ExcelJS from "exceljs";
import { Download, Sheet, FileText } from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  getBankAndBranchLogo,
  getThemeURL,
} from "../../../../../Services/EndPoints";
import { getData, postDataWithBody } from "../../../../../Services/Services";

const BarChart = ({
  data,
  formattedData,
  item,
  isExpanded,
  xAxisKey,
  yAxisKeys,
  colors,
  isViewMode,
  yAxisLabels,
  selectedBankIds,
  selectedBranchIds,
  editConfigIds
}) => {
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(
    isExpanded ? 400 : 300
  );
  const [yAxisWidth, setYAxisWidth] = useState(60);

  // Add these state variables with other useState hooks
  const [userProfile, setUserProfile] = useState(null);
  const [logoFromApi, setLogoFromApi] = useState(null);
  const [bankName, setBankName] = useState("");
  const [bankData, setBankData] = useState(null);
  const [shouldShowBankLogo, setShouldShowBankLogo] = useState(false);

  // Add these helper functions before the main return statement
  const getUserProfile = async () => {
    try {
      const profile = await getData(getThemeURL);
      if (profile && profile.length > 0) {
        setUserProfile(profile[0]);
        // After getting profile, fetch logo with proper payload
        if (item.showBankLogo) {
          fetchLogoAndNameFromApi(profile[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserProfile(null);
    }
  };

  const createPayload = (profile) => {
    let admin_role = "U"; // default value

    if (profile.is_admin === true) {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  const byteArrayToImageUrl = (byteArray) => {
    try {
      const uint8Array = new Uint8Array(byteArray);
      const blob = new Blob([uint8Array], { type: "image/png" });
      const imageUrl = URL.createObjectURL(blob);
      return imageUrl;
    } catch (error) {
      console.error("Error converting byte array to image:", error);
      return null;
    }
  };

    // Replace the checkSingleBankData function with this updated version
  const checkSingleBankData = () => {
    const dataToCheck = formattedData || data || [];
    if (!dataToCheck || dataToCheck.length === 0) return false;
  
    // PRIORITY 1: Use selectedBranchIds if available and single
    if (selectedBranchIds && selectedBranchIds.length === 1) {
      return true;
    }
  
    // PRIORITY 2: Use selectedBankIds if available and single
    if (selectedBankIds && selectedBankIds.length === 1) {
      return true;
    }
  
    // PRIORITY 3: Use editConfigIds as fallback if available and single
    if (editConfigIds && editConfigIds.length === 1) {
      return true;
    }
  
    // Extract unique bank IDs from the chart data as final fallback
    const bankIds = new Set();
  
    dataToCheck.forEach((row) => {
      // Check common bank ID field names
      const bankId = row.bank_id || row.bankId || row.Bank_ID;
      if (bankId) {
        bankIds.add(bankId);
      }
    });
  
    console.log("Bank IDs in chart data:", Array.from(bankIds));
  
    // Show logo if there's exactly one bank in the data
    return bankIds.size === 1;
  };
    const fetchLogoAndNameFromApi = async (profile) => {
    try {
      const payload = createPayload(profile);
      const response = await postDataWithBody(
        getBankAndBranchLogo,
        payload,
        {}
      );
  
      if (Array.isArray(response) && response.length > 0) {
        console.log(response, "logooooooooo3");
        let bankData = null;
  
        // Determine which bank data to use based on user role and chart data
        if (profile.role_name === "SUPER ADMIN" || profile.is_admin === true) {
          console.log(response, "logoooooooooo2");
          const shouldShowLogo = checkSingleBankData();
  
          if (shouldShowLogo) {
            console.log(response, "logoooooooooo1");
  
            // PRIORITY 1: Use selectedBranchIds if available and single
            if (selectedBranchIds && selectedBranchIds.length === 1) {
              const branchId = selectedBranchIds[0];
              console.log("Using single selectedBranchIds:", branchId);
  
              // Search through all banks and their branches
              for (const bank of response) {
                if (bank.branches && Array.isArray(bank.branches)) {
                  const matchingBranch = bank.branches.find(
                    (branch) =>
                      branch.id === branchId ||
                      branch.branch_id === branchId ||
                      branch.Branch_ID === branchId
                  );
  
                  if (matchingBranch) {
                    // Found the branch, use branch data
                    bankData = {
                      ...bank,
                      ...matchingBranch,
                      isBranch: true,
                    };
                    break;
                  }
                }
              }
            }
            // PRIORITY 2: Use selectedBankIds if available and single
            else if (selectedBankIds && selectedBankIds.length === 1) {
              const bankId = selectedBankIds[0];
              console.log("Using single selectedBankIds:", bankId);
  
              bankData = response.find(
                (bank) =>
                  bank.id === bankId ||
                  bank.bank_id === bankId ||
                  bank.Bank_ID === bankId
              );
            }
            // PRIORITY 3: Use editConfigIds as fallback if available and single
            else if (editConfigIds && editConfigIds.length === 1) {
              const configId = editConfigIds[0];
              console.log("Using single editConfigIds as fallback:", configId);
  
              // Try to find bank/branch by config ID
              bankData = response.find(
                (bank) =>
                  bank.id === configId ||
                  bank.bank_id === configId ||
                  bank.Bank_ID === configId ||
                  (bank.branches && bank.branches.some(
                    branch => branch.id === configId || branch.branch_id === configId
                  ))
              );
  
              // If we found a bank but need branch-specific data
              if (bankData && bankData.branches) {
                const matchingBranch = bankData.branches.find(
                  branch => branch.id === configId || branch.branch_id === configId
                );
                if (matchingBranch) {
                  bankData = {
                    ...bankData,
                    ...matchingBranch,
                    isBranch: true,
                  };
                }
              }
            }
            // PRIORITY 4: Fallback to bank_id from chart data
            else {
              const dataToCheck = formattedData || data || [];
              const tableIdToMatch = dataToCheck[0]?.bank_id || dataToCheck[0]?.bankId || dataToCheck[0]?.Bank_ID;
              console.log("Using bank_id as final fallback:", tableIdToMatch);
  
              if (tableIdToMatch) {
                bankData = response.find(
                  (bank) =>
                    bank.id === tableIdToMatch ||
                    bank.bank_id === tableIdToMatch ||
                    bank.Bank_ID === tableIdToMatch
                );
              }
            }
  
            console.log(bankData, "bankDataaaaaaaaa");
  
            // If no match found, use first available bank data
            if (!bankData && response.length > 0) {
              bankData = response[0];
            }
          } else {
            // Multiple banks or branches in selection, don't show logo
            setShouldShowBankLogo(false);
            return;
          }
        } else {
          // For non-Super Admin users, use the first response (their bank)
          bankData = response[0];
        }
  
        if (!bankData) {
          setShouldShowBankLogo(false);
          return;
        }
  
        setBankData(bankData);
        setShouldShowBankLogo(true);
  
        // Set bank/branch name
        if (bankData.branchName) {
          setBankName(bankData.branchName);
        } else if (bankData.bankName) {
          setBankName(bankData.bankName);
        } else {
          setBankName("");
        }
  
        // Check if image field exists and is an array with data
        // Priority: branch image -> bank image
        if (
          bankData.hasOwnProperty("image") &&
          Array.isArray(bankData.image) &&
          bankData.image.length > 0
        ) {
          const imageUrl = byteArrayToImageUrl(bankData.image);
          setLogoFromApi(imageUrl);
        } else if (
          bankData.bankLogo &&
          Array.isArray(bankData.bankLogo) &&
          bankData.bankLogo.length > 0
        ) {
          // Fallback to bank logo if branch logo not available
          const imageUrl = byteArrayToImageUrl(bankData.bankLogo);
          setLogoFromApi(imageUrl);
        } else {
          setLogoFromApi(null);
        }
      } else {
        setShouldShowBankLogo(false);
      }
    } catch (error) {
      console.error("Error fetching logo and bank name from API:", error);
      setLogoFromApi(null);
      setShouldShowBankLogo(false);
    }
  };
  
  // Update the other useEffect that depends on data changes
  useEffect(() => {
    if (
      item.showBankLogo &&
      userProfile &&
      userProfile.role_name === "SUPER ADMIN"
    ) {
      // Re-check if we should show logo when chart data changes
      const hasSingleBank = checkSingleBankData();
      setShouldShowBankLogo(hasSingleBank);
  
      if (hasSingleBank && bankData) {
        // Re-fetch logo for the specific bank if needed
        fetchLogoAndNameFromApi(userProfile);
      }
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedData, data, userProfile, selectedBankIds, selectedBranchIds, editConfigIds]); // Add all relevant dependencies
    useEffect(() => {
      if (item.showBankLogo) {
        getUserProfile();
      }
       // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.showBankLogo]);
  
    // Update the other useEffect that depends on data changes
  useEffect(() => {
    if (
      item.showBankLogo &&
      userProfile &&
      userProfile.role_name === "SUPER ADMIN"
    ) {
      // Re-check if we should show logo when chart data changes
      const hasSingleBank = checkSingleBankData();
      setShouldShowBankLogo(hasSingleBank);
  
      if (hasSingleBank && bankData) {
        // Re-fetch logo for the specific bank if needed
        fetchLogoAndNameFromApi(userProfile);
      }
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedData, data, userProfile, selectedBankIds, selectedBranchIds, editConfigIds]); // Add all relevant dependencies
    // Cleanup effect for object URLs
    useEffect(() => {
      return () => {
        if (logoFromApi) {
          URL.revokeObjectURL(logoFromApi);
        }
      };
    }, [logoFromApi]);
  
    // Add useEffect to re-check bank data when tableData changes
    useEffect(() => {
      if (
        item.showBankLogo &&
        userProfile &&
        userProfile.role_name === "SUPER ADMIN"
      ) {
        // Re-check if we should show logo when table data changes
        const hasSingleBank = checkSingleBankData();
        console.log("Re-checking bank data:", { hasSingleBank });
  
        if (hasSingleBank) {
          // Re-fetch logo for the specific bank if needed
          fetchLogoAndNameFromApi(userProfile);
        } else {
          setShouldShowBankLogo(false);
        }
      }
       // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formattedData, data, item.showBankLogo]); // Only depend on length to avoid infinite loops
  // Function to calculate the required Y-axis width based on data values
  const calculateYAxisWidth = () => {
    if (
      !formattedData ||
      formattedData.length === 0 ||
      !yAxisKeys ||
      yAxisKeys.length === 0
    ) {
      return 60; // Default width
    }

    let maxValue = 0;
    let minValue = 0;

    // Find the maximum and minimum values across all y-axis keys
    formattedData.forEach((dataPoint) => {
      yAxisKeys.forEach((key) => {
        const value = dataPoint[key];
        if (typeof value === "number" && !isNaN(value)) {
          maxValue = Math.max(maxValue, Math.abs(value));
          minValue = Math.min(minValue, value);
        }
      });
    });

    // Handle negative values
    const maxAbsValue = Math.max(Math.abs(maxValue), Math.abs(minValue));
    // Estimate character width (approximately 8 pixels per character)
    const charWidth = 8;
    // Convert to string to count characters
    let maxString = "";
    if (maxAbsValue >= 1000000) {
      // For large numbers, format with appropriate suffixes or scientific notation
      maxString = maxAbsValue.toExponential(2);
    } else {
      maxString = maxAbsValue.toString();
    }
    // Add extra space for negative sign if needed
    if (minValue < 0) {
      maxString = "-" + maxString;
    }
    // Calculate width: character count * character width + padding
    const calculatedWidth = Math.max(40, maxString.length * charWidth + 20);
    // Cap the maximum width to prevent excessive spacing
    return Math.min(calculatedWidth, 120);
  };
  // Update Y-axis width when data changes
  useEffect(() => {
    const newWidth = calculateYAxisWidth();
    setYAxisWidth(newWidth);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedData, yAxisKeys]);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Use the actual container height minus some padding for margins
        const newHeight = Math.max(200, rect.height - 0); // Minimum height of 200px
        setContainerHeight(newHeight);
      }
    };

    // Initial measurement
    updateDimensions();

    // Create ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      if (containerRef.current) {
         // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  // Also update height when isExpanded changes (fallback)
  useEffect(() => {
    if (!containerRef.current) {
      setContainerHeight(isExpanded ? 400 : 300);
    }
  }, [isExpanded]);

  const formatHeaderText = (text) => {
    if (!text || typeof text !== "string") return text;

    return text
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Chart Data");

    let currentRow = 1;

    // Add bank logo and name if enabled
    if (item.showBankLogo && shouldShowBankLogo) {
      try {
        // Add logo if available
        if (logoFromApi) {
          const response = await fetch(logoFromApi);
          const arrayBuffer = await response.arrayBuffer();

          const imageId = workbook.addImage({
            buffer: arrayBuffer,
            extension: "png",
          });

          worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 100 },
          });
        }

        // Add bank name if available (whether logo exists or not)
        if (bankName) {
          const bankNameCell = worksheet.getCell(logoFromApi ? "B1" : "A1");
          bankNameCell.value = bankName;
          bankNameCell.font = {
            bold: true,
            size: parseInt(
              bankData?.banknameFontsize || bankData?.branchNameFontsize || 16
            ),
          };
          bankNameCell.alignment = { vertical: "middle", horizontal: "left" };
        }

        // Set row height and spacing
        if (logoFromApi || bankName) {
          worksheet.getRow(1).height = 75;
          worksheet.getRow(2).height = 25;
          currentRow = 4;
        }
      } catch (error) {
        console.error("Error adding logo/name to Excel:", error);
        currentRow = 1;
      }
    }

    // Add title row if pdfChartTitle is available
    const titleText =
      item.pdfChartTitle !== undefined &&
      item.pdfChartTitle !== null &&
      item.pdfChartTitle.trim() !== ""
        ? item.pdfChartTitle.trim()
        : "";

    if (titleText) {
      // Add title row
      worksheet.getCell(`A${currentRow}`).value = titleText;

      // Style the title row
      const titleRow = worksheet.getRow(currentRow);
      titleRow.font = { bold: true, size: 16 };
      titleRow.alignment = { horizontal: "center" };

      // Merge cells for title (span across all columns)
      const totalColumns = yAxisKeys.length + 1; // +1 for xAxisKey
      worksheet.mergeCells(
        `A${currentRow}`,
        `${String.fromCharCode(64 + totalColumns)}${currentRow}`
      );

      currentRow += 2; // Add spacing after title
    }

    // Prepare header row with formatted text
    const headerRow = [
      formatHeaderText(xAxisKey),
      ...yAxisKeys.map((key, idx) =>
        formatHeaderText(yAxisLabels?.[idx] || key)
      ),
    ];
    worksheet.addRow(headerRow);

    // Style header row
    const headerRowIndex = currentRow;
    currentRow++;

    const headerRowObj = worksheet.getRow(headerRowIndex);
    headerRowObj.font = { bold: true };
    headerRowObj.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };

    // Prepare data rows
    if (!formattedData || formattedData.length === 0) {
      const naRow = new Array(headerRow.length).fill("NA");
      worksheet.addRow(naRow);
    } else {
      formattedData.forEach((rowObj) => {
        const rowArr = [];
        rowArr.push(rowObj[xAxisKey]);
        yAxisKeys.forEach((key) => {
          let val = rowObj[key];
          if (val === null || val === undefined) {
            val = "NA";
          } else if (typeof val === "boolean") {
            val = val ? "Yes" : "No";
          }
          rowArr.push(val);
        });
        worksheet.addRow(rowArr);
      });
    }

    worksheet.columns.forEach((column) => {
      column.width = 15;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const now = new Date();
    const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");
    const istTime = `${hours}${minutes}${seconds}`;
    const fileTimestamp = `${istDate}_${istTime}`;
    const fileTitle = (item.title || "chartdata")
      .replace(/\s+/g, "_")
      .toLowerCase();
    const filename = `${fileTitle}_${fileTimestamp}.xlsx`;

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    try {
      const doc = new jsPDF("landscape", "pt", "a4");
      let currentY = 50;

      // Set font
      let fontName = "Helvetica";
      doc.setFont(fontName);
      doc.setFontSize(13);

      const pdfChartTitle =
        item.pdfChartTitle !== undefined &&
        item.pdfChartTitle !== null &&
        item.pdfChartTitle.trim() !== ""
          ? item.pdfChartTitle.trim()
          : "";

      // Add bank logo and name if enabled (side by side, centered)
      if (
        item.showBankLogo &&
        shouldShowBankLogo &&
        (logoFromApi || bankName)
      ) {
        try {
          const pageWidth = doc.internal.pageSize.width;
          const centerX = pageWidth / 2;

          if (logoFromApi && bankName) {
            // Both logo and name exist - show side by side
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();
            img.crossOrigin = "anonymous";

            await new Promise((resolve, reject) => {
              img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                resolve();
              };
              img.onerror = reject;
              img.src = logoFromApi;
            });

            const logoDataURL = canvas.toDataURL("image/png");
            const logoWidth = 70;
            const logoHeight = 70;

            doc.setFontSize(16);
            doc.setFont(fontName, "bold");
            const bankNameFontSize =
              bankData?.banknameFontsize || bankData?.branchNameFontsize || 16;
            doc.setFontSize(parseInt(bankNameFontSize));

            const textWidth = doc.getTextWidth(bankName);
            const spacing = 15;
            const totalWidth = logoWidth + spacing + textWidth;
            const startX = centerX - totalWidth / 2;

            const logoX = startX;
            const logoY = currentY;
            const textX = startX + logoWidth + spacing;
            const textY = currentY + logoHeight / 2 + 6;

            doc.addImage(
              logoDataURL,
              "PNG",
              logoX,
              logoY,
              logoWidth,
              logoHeight
            );
            doc.text(bankName, textX, textY);

            currentY += Math.max(logoHeight, 40) + 20;
          } else if (bankName) {
            // Only bank name exists - show centered
            doc.setFont(fontName, "bold");
            const bankNameFontSize =
              bankData?.banknameFontsize || bankData?.branchNameFontsize || 16;
            doc.setFontSize(parseInt(bankNameFontSize));
            doc.text(bankName, centerX, currentY, { align: "center" });
            currentY += 40;
          }

          doc.setFont(fontName, "normal");
          doc.setFontSize(13);
        } catch (error) {
          console.error("Error adding logo/name to PDF:", error);
        }
      }

      // Add title
      const pageWidth = doc.internal.pageSize.width;
      doc.text(pdfChartTitle, pageWidth / 2, currentY, { align: "center" });
      currentY += 30;

      // Prepare headers and data rows
      const headers = [
        formatHeaderText(xAxisKey),
        ...yAxisKeys.map((key, idx) =>
          formatHeaderText(yAxisLabels?.[idx] || key)
        ),
      ];
      const dataRows = formattedData.map((row) => [
        row[xAxisKey],
        ...yAxisKeys.map((key) => {
          let val = row[key];
          if (val === null || val === undefined) return "NA";
          if (typeof val === "boolean") return val ? "Yes" : "No";
          return val.toString();
        }),
      ]);

      doc.autoTable({
        head: [headers],
        body: dataRows,
        startY: currentY,
        theme: "grid",
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 10, halign: "center" },
        margin: { left: 40, right: 40 },
        columnStyles: {
          0: {
            halign: (data) => {
              const cellValue = data.cell.raw;
              if (!isNaN(cellValue) && !isNaN(parseFloat(cellValue))) {
                return "center";
              } else {
                return "left";
              }
            },
          },
        },
      });

      // ADD FOOTER SECTION
      const pageHeight = doc.internal.pageSize.height;
      let footerY = pageHeight - 180;

      doc.setFontSize(10);
      doc.text("Letter No.: ", 50, footerY + 100);
      doc.text("Date: ", 50, footerY + 120);
      doc.text("Copy: ", 50, footerY + 140);
      doc.text("XXXX/ ", 550, footerY + 100);
      doc.text("XX(XXXX)(XXXX)/", 550, footerY + 120);
      doc.text("XXXX(XX)/", 550, footerY + 140);

      doc.setFont(fontName, "bold");
      doc.text("[ ]", 550, footerY + 160);
      doc.setFont(fontName, "normal");

      const now = new Date();
      const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const seconds = now.getSeconds().toString().padStart(2, "0");
      const istTime = `${hours}${minutes}${seconds}`;
      const fileTimestamp = `${istDate}_${istTime}`;
      const fileTitle = (item.title || "chart_report")
        .replace(/\s+/g, "_")
        .toLowerCase();
      const filename = `${fileTitle}_${fileTimestamp}.pdf`;

      doc.save(filename);
    } catch (error) {
      alert("Error generating PDF. Please try again.");
      console.error("PDF generation error:", error);
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        minHeight: "200px", // Ensure minimum height
        boxSizing: "border-box",
      }}
    >
      {(item.showDownloadExcelChart || item.showDownloadPDFChart) && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 10,
            gap: "10px",
          }}
        >
          {item.showDownloadExcelChart && (
            <button
              onClick={downloadExcel}
              style={{
                padding: "6px 6px",
                backgroundColor: "#10B981",
                color: "white",
                border: "none",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <Download size={16} />
              <Sheet size={16} />
            </button>
          )}
          {item.showDownloadPDFChart && (
            <button
              onClick={downloadPDF}
              style={{
                padding: "6px 6px",
                backgroundColor: "#EF4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <Download size={16} />
              <FileText size={16} />
            </button>
          )}
        </div>
      )}

      <ResponsiveContainer width="100%" height={containerHeight}>
        <RechartsBarChart
          data={formattedData}
          layout={item.layout || "vertical"}
          margin={{
            left: 5,
            right: 40,
            // bottom: 5
            bottom:
              item.xAxisOrientation === "vertical"
                ? 20
                : item.showDownloadExcelChart || item.showDownloadPDFChart
                ? 50
                : 5,
          }}
        >
          {item.gridLines !== false && (
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          )}
          <XAxis
            dataKey={xAxisKey}
            stroke="#666"
            angle={
              item.xAxisOrientation === "angled"
                ? -30
                : item.xAxisOrientation === "vertical"
                ? -90
                : 0
            }
            textAnchor={item.xAxisOrientation === "vertical" ? "end" : "middle"}
            height={
              item.xAxisOrientation === "angled"
                ? 70
                : item.xAxisOrientation === "vertical"
                ? 120
                : 60
            }
            tickMargin={
              item.xAxisOrientation === "vertical"
                ? 15
                : item.xAxisOrientation === "angled"
                ? 25
                : 20
            }
            style={{ fontSize: `${item.xAxisLabelFontSize}px` }}
          />
          <YAxis
            stroke="#666"
            width={yAxisWidth}
            style={{ fontSize: `${item.yAxisLabelFontSize}px` }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
            formatter={(value, name, props) => [
              value,
              item.yAxisLabels?.[props.dataKey] || name,
            ]}
          />
          {item.legendPosition !== "hidden" && (
            <Legend
              layout={
                item.legendPosition === "left" ||
                item.legendPosition === "right"
                  ? "vertical"
                  : "horizontal"
              }
              verticalAlign={
                item.legendPosition === "bottom"
                  ? "bottom"
                  : item.legendPosition === "top"
                  ? "top"
                  : "middle"
              }
              align={
                item.legendPosition === "left"
                  ? "left"
                  : item.legendPosition === "right"
                  ? "right"
                  : "center"
              }
              wrapperStyle={{
                paddingTop: item.legendPosition === "bottom" ? "10px" : "0px",
                paddingBottom: item.legendPosition === "top" ? "10px" : "0px",
                fontSize: `${item.legendFontSize}px`,
              }}
            />
          )}
          {isViewMode && (
            <Brush
              height={25}
              stroke="#8884d8"
              travellerWidth={15}
              startIndex={0}
              // endIndex={Math.min(10, data.length - 1)}
              endIndex={data.length - 1}
              tickFormatter={() => ""}
            />
          )}
          {yAxisKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={colors[index % colors.length]}
              radius={[4, 4, 0, 0]}
              name={yAxisLabels?.[index] || key}
              barSize={item.barWidth || 30}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart;
